import {
  CARD_IMAGE_RATIOS,
  CARD_SHOW_FIELDS,
  CATEGORIES_NAV,
  PRODUCT_DETAIL_LAYOUTS,
  PRODUCT_LIST_LAYOUTS,
  TOKEN_KEYS,
} from '@telestore/theming';
import { z } from 'zod';
import { fnbSettingsSchema } from './verticals.js';
import { currencySchema, imageSchema, localeSchema, planSchema, verticalSchema } from './common.js';

/**
 * Shop theme JSONB (CLAUDE.md §5.3). Overrides are a sparse map: the merchant
 * only ever sets a handful of tokens, everything else falls through to the
 * template. Unknown keys and CSS control characters are rejected here and
 * again in `resolveTheme` — a stale blob must never be able to inject CSS.
 */
export const themeTokensSchema = z
  .record(z.string().max(60), z.string().max(120))
  .refine((tokens) => Object.keys(tokens).every((k) => (TOKEN_KEYS as readonly string[]).includes(k)), {
    message: 'Unknown design token',
  })
  .refine((tokens) => Object.values(tokens).every((v) => !/[;{}<>]/.test(v)), {
    message: 'Token values may not contain CSS control characters',
  });

export const layoutConfigSchema = z.object({
  product_list: z.enum([...PRODUCT_LIST_LAYOUTS]).optional(),
  card_image_ratio: z.enum([...CARD_IMAGE_RATIOS]).optional(),
  card_show: z.array(z.enum([...CARD_SHOW_FIELDS])).max(5).optional(),
  categories_nav: z.enum([...CATEGORIES_NAV]).optional(),
  home_banner: z.boolean().optional(),
  product_detail: z.enum([...PRODUCT_DETAIL_LAYOUTS]).optional(),
});

export const shopThemeSchema = z.object({
  template: z.string().max(40).default('minimal'),
  overrides: themeTokensSchema.optional(),
  /**
   * §5 (v2): dark-mode-aware templates. `auto` follows the customer's device
   * and is what almost every shop wants; the other two exist for a brand that
   * only works one way round. Every template has a dark palette whether or not
   * anyone authored one, so this is a preference and never a missing feature.
   */
  colorScheme: z.enum(['auto', 'light', 'dark']).default('auto'),
  darkOverrides: themeTokensSchema.optional(),
  layout: layoutConfigSchema.optional(),
  logo: z.string().url().nullable().default(null),
  cover: z.string().url().nullable().default(null),
});
export type ShopThemeDto = z.infer<typeof shopThemeSchema>;

/**
 * Business hours, one row per weekday (0 = Sunday). Used by the tier-1 support
 * brain to answer "are you open?" without an AI call (§3 `support`).
 */
export const openingHoursSchema = z.array(
  z.object({
    weekday: z.number().int().min(0).max(6),
    open: z.string().regex(/^\d{2}:\d{2}$/),
    close: z.string().regex(/^\d{2}:\d{2}$/),
    closed: z.boolean().default(false),
  }),
);

/**
 * KHQR merchant identity for the `manual_qr` provider. The merchant reads
 * these off their ABA/Bakong app once during onboarding; without them we
 * cannot build a scannable QR, so the API refuses prepaid checkout with
 * `khqr_config_missing` rather than showing a broken code.
 */
export const khqrConfigSchema = z.object({
  /** Bakong account id, e.g. "chan_dara@aba". */
  bakong_account_id: z.string().min(3).max(64),
  /** Latin merchant name printed by the bank app, max 25 chars per EMVCo. */
  merchant_name: z.string().min(1).max(25),
  merchant_city: z.string().min(1).max(15).default('Phnom Penh'),
  /**
   * The account number the money lands in — EMV tag 29, sub-tag 01.
   *
   * NOT optional in practice, and proven so by a real interbank transfer on
   * 2026-08-21: a payload carrying only `bakong_account_id` was paid from
   * ACLEDA and never credited at ABA. `bakong_account_id` names the BANK
   * (`abaakhppxxx` is ABA's own BIC, the same string for every ABA customer);
   * this is what names the customer.
   *
   * Optional in the schema only so a shop configured before this was
   * understood still parses — `buildKhqrPayload` refuses to generate without
   * it, which turns a silent loss into a visible setup step.
   */
  account_number: z.string().min(4).max(34).optional(),
  /** Shown to the payer beside the account — EMV tag 29, sub-tag 02. */
  bank_name: z.string().max(32).optional(),
  /** Acquiring-bank merchant id, for a shop that registered as one (tag 30). */
  merchant_id: z.string().max(32).optional(),
  acquiring_bank: z.string().max(32).optional(),
  /** Shown under the QR so the customer can sanity-check the payee name. */
  account_label: z.string().max(60).optional(),
  /**
   * Which account template the merchant's own bank uses — EMV tag 29
   * (individual) or tag 30 (registered merchant).
   *
   * A FACT read off their QR, not something to infer. `buildKhqrPayload` used
   * to decide it from whether `merchant_id` happened to be set, and
   * `khqr.test.ts` records what that cost: "the field was there, and the tag it
   * went into was the wrong one."
   *
   * Deliberately no default. A shop configured before this existed must keep
   * building exactly the payload it built yesterday, so the old inference
   * survives as a fallback for those rows and nothing else.
   */
  account_tag: z.enum(['29', '30']).optional(),
  /**
   * The payload this config was imported from, when it came from a scanned QR.
   *
   * Kept because we have already had to re-read a payload once to learn
   * something new about it (tag 30, 2026-08-26). Without it, that means asking
   * every merchant to photograph their standee again.
   */
  source_payload: z.string().max(512).optional(),
});
export type KhqrConfig = z.infer<typeof khqrConfigSchema>;

export const shopSettingsSchema = z.object({
  /** "Chat with shop" target — §2: we never build a chat system. */
  contact_telegram_username: z.string().max(40).nullable().default(null),
  contact_phone: z.string().max(24).nullable().default(null),
  facebook_url: z.string().url().nullable().default(null),
  opening_hours: openingHoursSchema.default([]),
  return_policy: z
    .object({ km: z.string().max(4000).default(''), en: z.string().max(4000).default('') })
    .default({ km: '', en: '' }),
  /** Prepaid orders expire if unpaid; keeps stock from being held forever. */
  payment_window_minutes: z.number().int().min(5).max(1440).default(60),
  cod_enabled: z.boolean().default(true),
  prepaid_enabled: z.boolean().default(true),
  /** Auto-confirm incoming orders instead of waiting for a merchant tap. */
  auto_confirm_orders: z.boolean().default(false),
  low_stock_threshold: z.number().int().min(0).max(999).default(3),
  khqr: khqrConfigSchema.nullable().default(null),
  /**
   * §4 F&B: how the shop hands food over. Declared here rather than left as
   * loose JSON so a misspelt key is a parse error instead of a silently
   * disabled pickup — which is exactly how the demo seed once made every
   * order at a coffee shop fail validation.
   *
   * Nested camelCase against the surrounding snake_case is deliberate: this
   * block is `fnbSettingsSchema`, owned by the verticals contract, and the
   * resolver reads it by those names.
   */
  fnb: fnbSettingsSchema.partial().optional(),
});
export type ShopSettings = z.infer<typeof shopSettingsSchema>;

export const shopPublicSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  vertical: verticalSchema,
  currency: currencySchema,
  defaultLocale: localeSchema,
  theme: shopThemeSchema,
  contactTelegramUsername: z.string().nullable(),
  contactPhone: z.string().nullable(),
  openingHours: openingHoursSchema,
  returnPolicy: z.object({ km: z.string(), en: z.string() }),
  codEnabled: z.boolean(),
  prepaidEnabled: z.boolean(),
  botUsername: z.string().nullable(),
  exchangeRateUsdKhr: z.number(),
});
export type ShopPublic = z.infer<typeof shopPublicSchema>;

export const createShopSchema = z.object({
  name: z.string().min(2).max(80),
  slug: z.string().min(3).max(48).optional(),
  vertical: verticalSchema.default('retail'),
  currency: currencySchema.default('USD'),
  defaultLocale: localeSchema.default('km'),
});

export const updateShopSchema = z.object({
  name: z.string().min(2).max(80).optional(),
  slug: z.string().min(3).max(48).optional(),
  description: z.string().max(2000).nullable().optional(),
  currency: currencySchema.optional(),
  defaultLocale: localeSchema.optional(),
  theme: shopThemeSchema.partial().optional(),
  settings: shopSettingsSchema.partial().optional(),
});

export const shopAdminSchema = shopPublicSchema.extend({
  plan: planSchema,
  status: z.enum(['active', 'suspended', 'closed']),
  settings: shopSettingsSchema,
  createdAt: z.string().datetime(),
});

export const cover = imageSchema; // re-exported for convenience in admin forms
